import { useNavigate, useParams } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { trpcClient } from "@/lib/trpc";
import VendorForm, { type VendorFormData } from "./VendorForm";
import { toast } from "sonner";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import DetailNotFoundState from "@/components/shared/DetailNotFoundState";

export default function VendorEdit() {
  const { vendorId } = useParams({ strict: false }) as { vendorId: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: vendor, isLoading, error: detailError, refetch: refetchDetail } = useQuery({
    queryKey: ["vendor", vendorId],
    queryFn: () => trpcClient.vendors.getById.query({ id: vendorId }),
  });

  const handleSubmit = async (data: VendorFormData) => {
    try {
      await trpcClient.vendors.update.mutate({
        id: vendorId,
        data: data as any,
      });

      await queryClient.invalidateQueries({ queryKey: ["vendors"] });
      await queryClient.invalidateQueries({ queryKey: ["vendors-lookup"] });
      await queryClient.invalidateQueries({ queryKey: ["vendor", vendorId] });
      navigate({ to: "/vendors" });
    } catch (err: any) {
      toast.error(err?.message || "Cập nhật nhà gia công thất bại");
    }
  };

  // Gõ/dán URL có id sai (404) hoặc không tải được: không hiển thị khung tải vô hạn
  if (!isLoading && !vendor) {
    return (
      <DetailNotFoundState
        breadcrumbs={[{ label: "Nhà cung cấp", to: "/vendors" }]}
        entityLabel="Nhà cung cấp"
        backTo="/vendors"
        codeOrId={vendorId}
        error={detailError}
        onRetry={() => refetchDetail()}
      />
    );
  }
  if (isLoading || !vendor) {
    return (
      <div className="w-full space-y-4 p-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <StandardDetailLayout
      breadcrumbs={[
        { label: "Nhà Gia Công", to: "/vendors" },
        { label: vendor.name, to: `/vendors/${vendor.id}` },
        { label: "Chỉnh sửa" },
      ]}
      title={`Sửa ${vendor.code || vendor.name}`}
      onBack={() => navigate({ to: `/vendors/${vendor.id}` })}
      showContextPanel={false}
    >
      <div className="w-full p-4 animate-in fade-in duration-300">
        <VendorForm
          defaultValues={vendor as any}
          onSubmit={handleSubmit}
          onCancel={() => navigate({ to: `/vendors/${vendor.id}` })}
          submitLabel="Cập nhật"
        />
      </div>
    </StandardDetailLayout>
  );
}
